import { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useWeeklyReport, useSaveWeeklyNotes, useSetFeedbackStatus } from 'hooks/useWeeklyReports';
import type { WeekSelection } from 'services/api/weekly-report';
import { Alert } from 'components/ui/Alert';
import { Button } from 'components/ui/Button';
import { ChannelBreakdown } from 'components/weeklyreport/ChannelBreakdown';
import { CostStats } from 'components/weeklyreport/CostStats';
import { DomainBreakdown } from 'components/weeklyreport/DomainBreakdown';
import { HandoffStats } from 'components/weeklyreport/HandoffStats';
import { HourHistogram } from 'components/weeklyreport/HourHistogram';
import { KnownIssuesList } from 'components/weeklyreport/KnownIssuesList';
import { ReportSection } from 'components/weeklyreport/ReportSection';
import { SendReportModal } from 'components/weeklyreport/SendReportModal';
import { TopRoutes } from 'components/weeklyreport/TopRoutes';
import { WeeklyNotesField } from 'components/weeklyreport/WeeklyNotesField';

import { numberFormat } from './format';

const toWeekInput = (isoYear: number, isoWeek: number): string =>
  `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;

const parseWeekInput = (value: string): WeekSelection => {
  const match = /^(\d{4})-W(\d{2})$/.exec(value);
  if (!match) return null;
  return { isoYear: Number(match[1]), isoWeek: Number(match[2]) };
};

export const WeeklyReportPage = () => {
  const [selection, setSelection] = useState<WeekSelection>(null);
  const { data, isLoading, error } = useWeeklyReport(selection);

  const saveNotes = useSaveWeeklyNotes(selection);
  const saveIssues = useSaveWeeklyNotes(selection);
  const setStatus = useSetFeedbackStatus(selection);

  const [notesDraft, setNotesDraft] = useState('');
  const [issuesDraft, setIssuesDraft] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [excludeGreetings, setExcludeGreetings] = useState(true);

  useEffect(() => {
    if (data) {
      setNotesDraft(data.notes ?? '');
      setIssuesDraft(data.issuesNotes ?? '');
    }
  }, [data?.week.isoWeek, data?.week.isoYear]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <section className="space-y-6">
        <h1 className="font-display text-3xl font-semibold text-brand-blue">Veckorapport</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="space-y-6">
        <h1 className="font-display text-3xl font-semibold text-brand-blue">Veckorapport</h1>
        <Alert
          type="error"
          title="Kunde inte hämta veckorapporten"
          message="Något gick fel vid hämtningen. Försök igen om en stund."
        />
      </section>
    );
  }

  const { week, previousWeek, knownIssues, currentWeek, greetings } = data;

  const totals = excludeGreetings ? data.totalsExcludingGreetings : data.totals;
  const change = excludeGreetings ? data.changeExcludingGreetings : data.change;
  const topRoutes = excludeGreetings ? data.topRoutesExcludingGreetings : data.topRoutes;

  const busiestHour = Math.max(...totals.byHour.map((row) => row.count), 1);

  const shownWeek = selection ?? { isoYear: week.isoYear, isoWeek: week.isoWeek };
  const weekInputValue = toWeekInput(shownWeek.isoYear, shownWeek.isoWeek);
  const maxWeekInput = toWeekInput(currentWeek.isoYear, currentWeek.isoWeek);

  const pill =
    'rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40';
  const activePill =
    'rounded-lg border border-brand-blue bg-brand-blue/5 px-3 py-2 text-sm font-medium text-brand-blue hover:bg-brand-blue/10';

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-brand-blue">{week.label}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-sm text-slate-500">Jämfört med vecka {previousWeek.isoWeek}</p>
            {week.isCurrent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                pågående vecka · ofullständig data
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="week"
            value={weekInputValue}
            max={maxWeekInput}
            onChange={(e) => {
              const parsed = parseWeekInput(e.target.value);
              if (parsed) setSelection(parsed);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
            aria-label="Välj vecka"
          />
          <button
            type="button"
            onClick={() => setSelection(null)}
            disabled={selection === null}
            className={pill}
          >
            Senaste hela veckan
          </button>
          <button
            type="button"
            onClick={() =>
              setSelection({ isoYear: currentWeek.isoYear, isoWeek: currentWeek.isoWeek })
            }
            disabled={week.isCurrent}
            className={pill}
          >
            Denna vecka
          </button>
          {greetings.count > 0 && (
            <button
              type="button"
              onClick={() => setExcludeGreetings((value) => !value)}
              aria-pressed={excludeGreetings}
              className={excludeGreetings ? activePill : pill}
              title={
                excludeGreetings
                  ? 'Visa alla chattar, inklusive de som bara innehöll en hälsning'
                  : 'Dölj chattar där kunden bara skrev en hälsning och inget mer'
              }
            >
              {excludeGreetings
                ? `Hälsningar dolda (${numberFormat.format(greetings.count)})`
                : 'Visar alla chattar'}
            </button>
          )}
          <Button variant="primary" size="sm" onClick={() => setConfirmSend(true)}>
            Skicka rapport
          </Button>
        </div>
      </header>

      {totals.total === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <MessageSquare className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-slate-600">Inga chattar den här veckan.</p>
          <p className="mt-1 text-sm text-slate-400">
            {week.isCurrent
              ? 'Veckan pågår fortfarande — siffrorna fylls på efter hand.'
              : 'Välj en annan vecka ovan, eller "Denna vecka" för pågående data.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <MessageSquare className="h-4 w-4" /> Antal chattar
            </div>
            <p className="mt-1 text-4xl font-bold text-slate-900">
              {numberFormat.format(totals.total)}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {change.percent === null
                ? 'Ingen jämförelse — förra veckan hade inga chattar'
                : `${change.total >= 0 ? '+' : ''}${numberFormat.format(change.total)} (${change.percent} %) mot vecka ${previousWeek.isoWeek}`}
            </p>
            {greetings.count > 0 && (
              <p className="mt-1 text-sm text-slate-500">
                {excludeGreetings
                  ? `${numberFormat.format(greetings.count)} chattar med enbart en hälsning är exkluderade.`
                  : `Inklusive ${numberFormat.format(greetings.count)} chattar som bara innehöll en hälsning.`}
              </p>
            )}
          </div>

          <ReportSection title="Per kanal">
            <ChannelBreakdown byChannel={totals.byChannel} />
          </ReportSection>

          <ReportSection title="Överlämningar" plain>
            <HandoffStats handoff={totals.handoff} emailDraft={totals.emailDraft} />
          </ReportSection>

          <ReportSection title="AI-kostnad" plain>
            <CostStats
              cost={totals.cost}
              tokens={totals.tokens}
              greetingsExcludedFromView={excludeGreetings && greetings.count > 0}
            />
          </ReportSection>

          <ReportSection title="Topp routes (efter antal anrop)">
            <TopRoutes routes={topRoutes} />
          </ReportSection>

          <ReportSection title="Per domän">
            <DomainBreakdown rows={totals.byDomain} withoutDomain={totals.withoutDomain} />
          </ReportSection>

          <ReportSection title="När på dygnet">
            <HourHistogram rows={totals.byHour} busiestHour={busiestHour} />
          </ReportSection>
        </>
      )}

      <ReportSection title="Vad gick fel / vad som fixats">
        <KnownIssuesList
          issues={knownIssues}
          onToggleStatus={(issue) =>
            setStatus.mutate({
              id: issue.id,
              status: issue.status === 'resolved' ? 'new' : 'resolved',
            })
          }
          isToggling={setStatus.isPending}
        />

        <div className="mt-4 border-t border-slate-100 pt-4">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Sammanfattning – vad gick fel / vad som fixats (följer med i mejlet)
          </label>
          <WeeklyNotesField
            value={issuesDraft}
            onChange={setIssuesDraft}
            onSave={() =>
              saveIssues.mutate({
                year: week.isoYear,
                week: week.isoWeek,
                issuesNotes: issuesDraft,
              })
            }
            rows={3}
            placeholder="Skriv en kort sammanfattning…"
            isPending={saveIssues.isPending}
            isSuccess={saveIssues.isSuccess}
            buttonClassName={pill}
          />
        </div>
      </ReportSection>

      <ReportSection title="Övrigt">
        <WeeklyNotesField
          value={notesDraft}
          onChange={setNotesDraft}
          onSave={() =>
            saveNotes.mutate({ year: week.isoYear, week: week.isoWeek, notes: notesDraft })
          }
          rows={4}
          placeholder="Fri kommentar som följer med i mejlet..."
          isPending={saveNotes.isPending}
          isSuccess={saveNotes.isSuccess}
          buttonClassName={pill}
        />
      </ReportSection>

      {confirmSend && (
        <SendReportModal
          weekLabel={week.label}
          isoYear={week.isoYear}
          isoWeek={week.isoWeek}
          greetingsCount={greetings.count}
          excludeGreetings={excludeGreetings}
          onClose={() => setConfirmSend(false)}
        />
      )}
    </section>
  );
};
